import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Capacitor } from '@capacitor/core';
import { App as NativeApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import type { PluginListenerHandle } from '@capacitor/core';
import { NATIVE_OAUTH_SCHEME } from './services/nativeAuth';
import LandingPage from './pages/LandingPage';
import BlogPage from './pages/BlogPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import ProfileSetupPage from './pages/ProfileSetupPage';
import DashboardPage from './pages/DashboardPage';
import DailyViewPage from './pages/DailyViewPage';
import MonthlyViewPage from './pages/MonthlyViewPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import SupportPage from './pages/SupportPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import DisclaimerPage from './pages/DisclaimerPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import SignInPage from './pages/SignInPage';
import Header from './components/Header';
import DocumentMetadata from './components/DocumentMetadata';
import WebsiteAnalytics from './components/WebsiteAnalytics';

const TodayPreviewPage = process.env.NODE_ENV === 'development'
  ? React.lazy(() => import('./pages/TodayPreviewPage'))
  : null;

const FeedingRhythmPreview = process.env.NODE_ENV === 'development'
  ? React.lazy(() => import('./pages/FeedingRhythmPreview'))
  : null;

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/" />;
  if (!user?.zipcode) return <Navigate to="/setup" />;
  return <>{children}</>;
}

function AuthOnly({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/" />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isAdmin } = useAuth();
  if (!isAuthenticated) return <Navigate to="/" />;
  if (!isAdmin || Capacitor.isNativePlatform()) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  return !isAuthenticated ? <>{children}</> : <Navigate to="/dashboard" />;
}

/** Starts each new navigation at the top; back/forward keeps the browser's restored position. */
function ScrollToTop() {
  const { key, hash } = useLocation();
  const navigationType = useNavigationType();
  useEffect(() => {
    if (navigationType !== 'POP' && !hash) window.scrollTo(0, 0);
  }, [key, hash, navigationType]);
  return null;
}

function NativeOAuthRedirectHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let listener: PluginListenerHandle | undefined;
    let disposed = false;
    let handledUrl: string | undefined;

    const handleUrl = (url: string) => {
      let deepLink: URL;
      try {
        deepLink = new URL(url);
      } catch (error) {
        console.error('Unable to parse app link:', error);
        return;
      }

      if (
        `${deepLink.protocol}` !== NATIVE_OAUTH_SCHEME ||
        deepLink.hostname !== 'auth' ||
        deepLink.pathname !== '/callback'
      ) {
        return;
      }

      if (handledUrl === url) return;
      handledUrl = url;

      void Browser.close().catch((error) => {
        console.error('Unable to close sign-in browser:', error);
      });
      navigate(`/auth/callback${deepLink.search}`);
    };

    const registerListener = async () => {
      try {
        const registeredListener = await NativeApp.addListener('appUrlOpen', ({ url }) => handleUrl(url));
        if (disposed) {
          void registeredListener.remove();
        } else {
          listener = registeredListener;
          const launchUrl = await NativeApp.getLaunchUrl();
          if (!disposed && launchUrl) handleUrl(launchUrl.url);
        }
      } catch (error) {
        console.error('Unable to initialize app-link handler:', error);
      }
    };

    void registerListener();

    return () => {
      disposed = true;
      if (listener) void listener.remove();
    };
  }, [navigate]);

  return null;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <NativeOAuthRedirectHandler />
        <DocumentMetadata />
        <ScrollToTop />
        <div className="app">
          <a href="#main-content" className="skip-link">Skip to main content</a>
          <Header />
          <main className="main-content" id="main-content" tabIndex={-1}>
            <Routes>
              {FeedingRhythmPreview && (
                <Route path="/preview/feeding-rhythm" element={
                  <React.Suspense fallback={<p role="status">Loading preview...</p>}>
                    <FeedingRhythmPreview />
                  </React.Suspense>
                } />
              )}
              {TodayPreviewPage && (
                <Route
                  path="/preview/:view"
                  element={<React.Suspense fallback={<p role="status">Loading preview...</p>}><TodayPreviewPage /></React.Suspense>}
                />
              )}
              <Route
                path="/setup"
                element={
                  <AuthOnly>
                    <ProfileSetupPage />
                  </AuthOnly>
                }
              />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <DashboardPage view="home" />
                  </PrivateRoute>
                }
              />
              <Route
                path="/outlook"
                element={<PrivateRoute><DashboardPage /></PrivateRoute>}
              />
              <Route
                path="/daily"
                element={
                  <PrivateRoute>
                    <DailyViewPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/monthly"
                element={
                  <PrivateRoute>
                    <MonthlyViewPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <PrivateRoute>
                    <ProfilePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminPage />
                  </AdminRoute>
                }
              />
              {!Capacitor.isNativePlatform() && (
                <>
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/blog/:slug" element={<BlogPage />} />
                </>
              )}
              <Route path="/optin" element={<LandingPage testerOptIn />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/disclaimer" element={<DisclaimerPage />} />
              <Route path="/delete-account" element={<DeleteAccountPage />} />
              <Route
                path="/signin"
                element={
                  <PublicRoute>
                    <SignInPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/"
                element={
                  <PublicRoute>
                    <LandingPage />
                  </PublicRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <WebsiteAnalytics />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
